import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TeamMembers = () => {
  const teamMembers = [
    {
      id: 1,
      name: 'Dr. Ahmad Rahman',
      position: 'Medical Director & Senior Consultant',
      specialization: 'Gastroenterology & Endoscopy',
      email: 'ahmad.rahman@endosurgery.com.my',
      phone: '+603-6157 8891',
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=400&fit=crop&crop=face',
      expertise: ['Advanced Endoscopy', 'ERCP Procedures', 'Medical Device Training'],
      languages: ['English', 'Bahasa Malaysia', 'Mandarin']
    },
    {
      id: 2,
      name: 'Sarah Lim Wei Ming',
      position: 'Sales Director',
      specialization: 'Medical Device Sales & Business Development',
      email: 'sarah.lim@endosurgery.com.my',
      phone: '+603-6157 8892',
      image: 'https://images.unsplash.com/photo-1594824388853-d0c2d4e5b4b5?w=400&h=400&fit=crop&crop=face',
      expertise: ['Hospital Procurement', 'Product Demonstrations', 'Market Analysis'],
      languages: ['English', 'Bahasa Malaysia', 'Mandarin', 'Cantonese']
    },
    {
      id: 3,
      name: 'Rajesh Kumar',
      position: 'Technical Support Manager',
      specialization: 'Medical Equipment Maintenance & Training',
      email: 'rajesh.kumar@endosurgery.com.my',
      phone: '+603-6157 8893',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
      expertise: ['Equipment Calibration', 'Technical Training', 'Preventive Maintenance'],
      languages: ['English', 'Bahasa Malaysia', 'Tamil', 'Hindi']
    },
    {
      id: 4,
      name: 'Nurul Aisyah Binti Hassan',
      position: 'Customer Relations Manager',
      specialization: 'Client Support & Product Education',
      email: 'nurul.aisyah@endosurgery.com.my',
      phone: '+603-6157 8894',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
      expertise: ['Customer Training', 'Product Education', 'Quality Assurance'],
      languages: ['Bahasa Malaysia', 'English', 'Arabic']
    }
  ];

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-text-primary mb-3 flex items-center justify-center">
          <Icon name="Users" size={24} className="mr-3" />
          Meet Our Expert Team
        </h2>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Our dedicated team of medical professionals and technical experts are here to support your healthcare needs 
          with personalized service and deep industry knowledge.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {teamMembers?.map((member, index) => (
          <motion.div
            key={member?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-card border border-border rounded-lg p-6 shadow-medical hover:shadow-medical-hover transition-all duration-300"
          >
            <div className="flex items-start space-x-4">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <Image
                    src={member?.image}
                    alt={member?.name}
                    className="w-20 h-20 rounded-full object-cover border-2 border-primary/20"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-white flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Member Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-text-primary mb-1">
                  {member?.name}
                </h3>
                <p className="text-primary font-medium text-sm mb-1">
                  {member?.position}
                </p>
                <p className="text-text-secondary text-sm mb-3">
                  {member?.specialization}
                </p>

                {/* Contact Info */}
                <div className="space-y-2 mb-4">
                  <a
                    href={`mailto:${member?.email}`}
                    className="flex items-center space-x-2 text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                  >
                    <Icon name="Mail" size={14} />
                    <span>{member?.email}</span>
                  </a>
                  <a
                    href={`tel:${member?.phone}`}
                    className="flex items-center space-x-2 text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                  >
                    <Icon name="Phone" size={14} />
                    <span>{member?.phone}</span>
                  </a>
                </div>

                {/* Expertise Tags */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-text-primary mb-2">Expertise:</p>
                  <div className="flex flex-wrap gap-1">
                    {member?.expertise?.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Languages */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-text-primary mb-2">Languages:</p>
                  <div className="flex flex-wrap gap-1">
                    {member?.languages?.map((language, langIndex) => (
                      <span
                        key={langIndex}
                        className="inline-block px-2 py-1 bg-muted text-text-secondary text-xs rounded-full"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <a
                    href={`mailto:${member?.email}`}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors duration-200"
                  >
                    <Icon name="Mail" size={12} />
                    <span>Email</span>
                  </a>
                  <a
                    href={`tel:${member?.phone}`}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-success text-white text-xs rounded-lg hover:bg-success/90 transition-colors duration-200"
                  >
                    <Icon name="Phone" size={12} />
                    <span>Call</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Team Availability Notice */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="Clock" size={24} color="var(--color-primary)" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-primary mb-2">
              Team Availability
            </h3>
            <div className="text-text-secondary text-sm space-y-1">
              <p><strong>Regular Hours:</strong> Monday - Friday, 8:30 AM - 5:30 PM (GMT+8)</p>
              <p><strong>Saturday:</strong> 8:30 AM - 1:00 PM (GMT+8)</p>
              <p><strong>Emergency Support:</strong> 24/7 for critical medical device issues</p>
              <p><strong>Response Time:</strong> Within 2 hours during business hours, 24 hours for non-urgent inquiries</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMembers;